import {
  Body2,
  Card,
  CardHeader,
  Tab,
  TabList,
  Text,
  Title3,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { type CSSProperties, type MouseEvent, type ReactNode } from "react";
import {
  AuthenticatedLayout,
  type AuthenticatedLayoutProps,
} from "./AuthenticatedLayout";

// ─── Public types ─────────────────────────────────────────────────────────────

export interface BackOfficeNavigationItem {
  key: string;
  label: string;
  ariaLabel?: string;
  disabled?: boolean;
  href?: string;
  icon?: ReactNode;
}

export interface BackOfficeNavigationProps {
  items: BackOfficeNavigationItem[];
  ariaLabel?: string;
  onNavigate?: (
    item: BackOfficeNavigationItem,
    event: MouseEvent<HTMLElement>,
  ) => void;
  selectedKey?: string;
}

export interface BackOfficeShellProps
  extends Omit<AuthenticatedLayoutProps, "navigation"> {
  navigation?: ReactNode;
  navigationAriaLabel?: string;
  navigationItems?: BackOfficeNavigationItem[];
  onNavigate?: BackOfficeNavigationProps["onNavigate"];
  selectedNavigationKey?: string;
  /** When true collapses the sidebar to icon-only width. */
  navCollapsed?: boolean;
}

export interface BackOfficePageProps {
  children: ReactNode;
  actions?: ReactNode;
  description?: ReactNode;
  title: ReactNode;
}

export interface BackOfficeSectionProps {
  children: ReactNode;
  actions?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  title?: ReactNode;
}

export interface BackOfficeCardGridProps {
  children: ReactNode;
  minColumnWidth?: CSSProperties["width"];
}

export interface BackOfficeMetricCardProps {
  helper?: ReactNode;
  label: ReactNode;
  value: ReactNode;
}

// ─── Navigation styles — matches legacy appNavTabListItem exactly ─────────────

const useNavStyles = makeStyles({
  // Wrapper nav element — padding/scrollbar as per ostroAppNavigation
  navWrapper: {
    paddingTop: "2px",
    paddingBottom: tokens.spacingVerticalL,
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflowY: "auto",
    // Slim scrollbar
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: tokens.colorNeutralStroke2,
      borderRadius: "4px",
    },
  },

  // Each Tab item — matches appNavTabListItem
  tabItem: {
    padding: "0",
    height: "40px",
    width: "100%",
    gap: "0px",
    // Icon slot
    "& .fui-Tab__icon": {
      fontSize: "20px",
      height: "100%",
      width: "44px",    // calc(48px - 4px)
      minWidth: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    // Text content slot
    "& .fui-Tab__content": {
      margin: "0 0 0 -3px",
    },
    // Active indicator (left accent bar)
    "::after": {
      width: "3px",
      borderRadius: "0.5rem",
      top: tokens.spacingVerticalSNudge,
      bottom: tokens.spacingVerticalSNudge,
      left: "4px",
      backgroundColor: tokens.colorCompoundBrandStroke,
    },
    // Selected state
    "&[aria-selected='true']": {
      backgroundColor: tokens.colorNeutralBackground3Selected,
      "& .fui-Tab__content": {
        color: tokens.colorCompoundBrandForeground1,
      },
      ":hover": {
        backgroundColor: tokens.colorNeutralBackground2Selected,
        "& .fui-Tab__icon": {
          color: tokens.colorCompoundBrandForeground1,
        },
        "& .fui-Tab__content": {
          color: `${tokens.colorCompoundBrandForeground1} !important`,
        },
      },
    },
    // Hover state
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    ":hover:active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
});

// ─── Page / section layout styles ─────────────────────────────────────────────

const pageStyle: CSSProperties = {
  boxSizing: "border-box", width: "100%", minWidth: 0, overflowX: "hidden",
  display: "flex", flexDirection: "column",
  gap: "1rem", padding: "clamp(8px, 2vw, 16px)",
};

const pageHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "1rem",
  paddingBottom: "0.5rem",
};

const pageTitleStyle: CSSProperties = {
  margin: 0,
  display: "block",
};

const pageDescriptionStyle: CSSProperties = {
  display: "block",
  marginTop: "0.125rem",
  color: "var(--colorNeutralForeground2)",
};

const sectionStyle: CSSProperties = {
  padding: "1rem",
};

const sectionHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "1rem",
};

const metricValueStyle: CSSProperties = {
  margin: "0.25rem 0",
};

// ─── BackOfficeNavigation ─────────────────────────────────────────────────────

export const BackOfficeNavigation = ({
  items,
  ariaLabel = "Back-office navigation",
  onNavigate,
  selectedKey,
}: BackOfficeNavigationProps): React.JSX.Element => {
  const styles = useNavStyles();

  return (
    <nav aria-label={ariaLabel} className={styles.navWrapper}>
      <TabList
        appearance="subtle"
        vertical
        selectedValue={selectedKey ?? ""}
        style={{ flex: 1 }}
      >
        {items.map((item) => (
          <Tab
            key={item.key}
            value={item.key}
            disabled={item.disabled}
            aria-label={item.ariaLabel ?? item.label}
            aria-current={item.key === selectedKey ? "page" : undefined}
            className={styles.tabItem}
            icon={item.icon ? <span>{item.icon}</span> : undefined}
            onClick={(event: MouseEvent<HTMLElement>) => {
              if (item.disabled) return;
              onNavigate?.(item, event);
            }}
          >
            {item.label}
          </Tab>
        ))}
      </TabList>
    </nav>
  );
};

// ─── BackOfficeShell ──────────────────────────────────────────────────────────

export const BackOfficeShell = ({
  children,
  navigation,
  navigationAriaLabel,
  navigationItems,
  onNavigate,
  selectedNavigationKey,
  navCollapsed,
  ...layoutProps
}: BackOfficeShellProps): React.JSX.Element => {
  const resolvedNavigation =
    navigation ??
    (navigationItems ? (
      <BackOfficeNavigation
        ariaLabel={navigationAriaLabel}
        items={navigationItems}
        onNavigate={onNavigate}
        selectedKey={selectedNavigationKey}
      />
    ) : undefined);

  return (
    <AuthenticatedLayout {...layoutProps} navigation={resolvedNavigation} navCollapsed={navCollapsed}>
      {children}
    </AuthenticatedLayout>
  );
};

// ─── BackOfficePage ───────────────────────────────────────────────────────────

export const BackOfficePage = ({
  children,
  actions,
  description,
  title,
}: BackOfficePageProps): React.JSX.Element => (
  <section style={pageStyle}>
    <header style={pageHeaderStyle}>
      <div>
        <Title3 as="h2" style={pageTitleStyle}>
          {title}
        </Title3>
        {description ? (
          <Body2 style={pageDescriptionStyle}>{description}</Body2>
        ) : null}
      </div>
      {actions ? <div>{actions}</div> : null}
    </header>
    {children}
  </section>
);

// ─── BackOfficeSection ────────────────────────────────────────────────────────

export const BackOfficeSection = ({
  children,
  actions,
  description,
  footer,
  title,
}: BackOfficeSectionProps): React.JSX.Element => (
  <Card style={sectionStyle}>
    {title || description || actions ? (
      <div style={sectionHeaderStyle}>
        <CardHeader
          header={title ? <Title3 as="h3">{title}</Title3> : undefined}
          description={description ? <Text>{description}</Text> : undefined}
        />
        {actions ? <div>{actions}</div> : null}
      </div>
    ) : null}
    {children}
    {footer ? <footer>{footer}</footer> : null}
  </Card>
);

// ─── BackOfficeCardGrid ───────────────────────────────────────────────────────

export const BackOfficeCardGrid = ({
  children,
  minColumnWidth = "12rem",
}: BackOfficeCardGridProps): React.JSX.Element => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}, 1fr))`,
      gap: "1rem",
    }}
  >
    {children}
  </div>
);

// ─── BackOfficeMetricCard ─────────────────────────────────────────────────────

export const BackOfficeMetricCard = ({
  helper,
  label,
  value,
}: BackOfficeMetricCardProps): React.JSX.Element => (
  <Card style={sectionStyle}>
    <Text size={200} weight="semibold">
      {label}
    </Text>
    <Title3 as="p" style={metricValueStyle}>
      {value}
    </Title3>
    {helper ? <Text size={200}>{helper}</Text> : null}
  </Card>
);
